import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, OnInit, output, signal } from '@angular/core';
import { FormField, form } from '@angular/forms/signals';
import { Subject, Subscription, debounceTime, firstValueFrom } from 'rxjs';
import { RecruitGroupResponseDto } from '@/app/common/general-types/recruit-groups.dto';
import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import { RecruitGroupService } from '@/app/services/recruit-group/recruit-group.service';
import { SocialScreeningService } from '@/app/services/social-screening/social-screening.service';
import { SocialMonitoring } from '@/app/services/social-monitoring/social-monitoring';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { LoadingOverlayComponent } from '@/app/atoms/loading-overlay/loading-overlay.component';
import { FormatService } from '@/app/common/format/format.service';
import { ImageService } from '@/app/services/image/image.service';
import { FirebaseService } from '@/app/common/firebase/firebase.service';

export type GroupPickerMode = 'attach-to-brief' | 'add-creators' | 'browse';

export interface GroupPickerBriefContext {
  kind: 'screening' | 'monitoring';
  briefId: string;
}

/**
 * The single place to pick a creator group.
 *
 * Replaces the separate list pickers that screening, monitoring and the creator-search
 * basket each had. Recruit groups are the only creator-collection construct now, and a
 * group is purpose-agnostic — the same group can back a screening brief and a monitoring
 * brief — so one picker serves every context.
 */
@Component({
  selector: 'rg-group-picker',
  imports: [FormField, LoadingOverlayComponent],
  templateUrl: './recruit-group-picker.html',
  styleUrl: './recruit-group-picker.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecruitGroupPicker implements OnInit {
  /** What picking a group should do. */
  readonly mode = input<GroupPickerMode>('browse');
  /** Required by 'attach-to-brief': which brief the group is being attached to. */
  readonly briefContext = input<GroupPickerBriefContext | null>(null);
  /** Required by 'add-creators': the creators staged for adding. */
  readonly creatorsToAdd = input<CreatorResponseDto[]>([]);
  readonly allowCreate = input(true);
  readonly selectedGroupId = model<string | null>(null);

  readonly attached = output<RecruitGroupResponseDto>();
  readonly creatorsAdded = output<{ group: RecruitGroupResponseDto; count: number }>();
  readonly closed = output<void>();

  readonly searchQueryModel = signal({ query: '' });
  readonly searchQueryForm = form(this.searchQueryModel);

  readonly createFormModel = signal({ groupName: '', groupDescription: '' });
  readonly createForm = form(this.createFormModel);
  readonly showCreateForm = signal(false);

  readonly busy = signal(false);
  readonly loaded = signal(false);
  private readonly searchQuery = signal('');
  private readonly searchSubject = new Subject<string>();
  private readonly subscriptions = new Subscription();

  readonly displayGroups = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const groups = this.recruitGroupService.visibleGroups();
    if (!query) return groups;
    return groups.filter((g) => (g.groupName ?? '').toLowerCase().includes(query));
  });

  readonly selectedGroup = computed(() =>
    this.recruitGroupService.visibleGroups().find((g) => g.id === this.selectedGroupId()) ?? null,
  );

  readonly confirmLabel = computed(() => {
    if (this.mode() === 'add-creators') {
      const count = this.creatorsToAdd().length;
      return `Add ${count} creator${count === 1 ? '' : 's'} to this group`;
    }
    return 'Use this group';
  });

  /**
   * Attaching a shared group fans out to every brief already using it, so say so rather
   * than surprising the user.
   */
  readonly fanOutWarning = computed(() => {
    if (this.mode() !== 'add-creators') return null;
    const briefs = this.recruitGroupService.groupUsageBriefs(this.selectedGroup());
    if (briefs.length === 0) return null;
    return `These creators will also be added to ${briefs.length} brief${briefs.length === 1 ? '' : 's'} using this group.`;
  });

  readonly recruitGroupService = inject(RecruitGroupService);
  private readonly screeningService = inject(SocialScreeningService);
  private readonly monitoringService = inject(SocialMonitoring);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly toast = inject(ToastNotificationService);
  private readonly firebaseService = inject(FirebaseService);
  readonly formatService = inject(FormatService);
  readonly imageService = inject(ImageService);

  constructor() {
    this.subscriptions.add(
      this.searchSubject.pipe(debounceTime(150)).subscribe((query) => this.searchQuery.set(query)),
    );
    effect(() => this.searchSubject.next(this.searchQueryModel().query));
  }

  ngOnInit(): void {
    this.recruitGroupService.initRecruitmentGroups().finally(() => this.loaded.set(true));
  }

  selectGroup(group: RecruitGroupResponseDto): void {
    this.selectedGroupId.set(group.id);
  }

  usageBriefs(group: RecruitGroupResponseDto) {
    return this.recruitGroupService.groupUsageBriefs(group);
  }

  isLocked(group: RecruitGroupResponseDto): boolean {
    return this.recruitGroupService.isGroupLocked(group);
  }

  toggleCreateForm(): void {
    this.showCreateForm.update((open) => !open);
  }

  async createGroup(): Promise<void> {
    const { groupName, groupDescription } = this.createFormModel();
    if (!groupName.trim()) return;

    this.busy.set(true);
    try {
      const created = await firstValueFrom(
        this.recruitGroupService.createRecruitGroup({
          groupName: groupName.trim(),
          groupDescription: groupDescription.trim(),
          businessAccountId: this.currentUserService.focussedBrand?.id,
          createdBy: this.firebaseService.userId as string,
          isGlobal: false,
        }),
      );
      await this.recruitGroupService.initRecruitmentGroups();
      this.showCreateForm.set(false);
      this.createFormModel.set({ groupName: '', groupDescription: '' });
      if (created?.id) this.selectedGroupId.set(created.id);
    } catch {
      this.toast.alert({
        title: 'Whoops! Something went wrong.',
        message: 'The group could not be created. Please try again.',
        type: 'error',
        icon: 'error',
      });
    } finally {
      this.busy.set(false);
    }
  }

  confirm(): void {
    const group = this.selectedGroup();
    if (!group) return;
    if (this.mode() === 'add-creators') {
      this.addCreators(group);
    } else if (this.mode() === 'attach-to-brief') {
      this.attachToBrief(group);
    } else {
      this.attached.emit(group);
    }
  }

  private addCreators(group: RecruitGroupResponseDto): void {
    const creatorIds = this.creatorsToAdd()
      .map((c) => c.blazeFirebaseUid)
      .filter((id): id is string => !!id);
    if (!creatorIds.length) return;

    this.busy.set(true);
    this.recruitGroupService.addCreatorsToGroup(group.id, creatorIds).subscribe({
      next: (result) => {
        this.busy.set(false);
        this.creatorsAdded.emit({ group, count: result.added.length });
        this.toast.alert({
          title: 'Creators added',
          message: this.addedMessage(result.added.length, result.alreadyPresent.length)
            + (result.noodleSyncFailed ? ' They were saved, but could not be synced for screening yet — contact support to resync this group.' : ''),
          type: 'success',
          icon: 'success',
        });
      },
      error: () => {
        this.busy.set(false);
        this.toast.alert({
          title: 'Whoops! Something went wrong.',
          message: 'The creators could not be added. Please try again.',
          type: 'error',
          icon: 'error',
        });
      },
    });
  }

  private addedMessage(added: number, alreadyPresent: number): string {
    if (added === 0 && alreadyPresent > 0) return 'Those creators are already in this group.';
    const suffix = alreadyPresent > 0 ? ` ${alreadyPresent} were already in the group.` : '';
    return `${added} creator${added === 1 ? '' : 's'} added.${suffix}`;
  }

  private attachToBrief(group: RecruitGroupResponseDto): void {
    const context = this.briefContext();
    if (!context) return;

    this.busy.set(true);
    const businessAccountId = this.currentUserService.focussedBrand?.id;
    const request$ =
      context.kind === 'screening'
        ? this.screeningService.addListToScreeningBrief({
            screeningBriefId: context.briefId,
            recruitGroupId: group.id,
            businessAccountId,
          })
        : this.monitoringService.addListToMonitoringBrief({
            monitoringBriefId: context.briefId,
            recruitGroupId: group.id,
            businessAccountId,
          });

    request$.subscribe({
      next: () => {
        this.busy.set(false);
        this.attached.emit(group);
        this.toast.alert({
          title: 'Creator group set successfully.',
          message: (group.numberOfCreators ?? 0) > 0
            ? 'The creator group has been linked to this brief.'
            : 'The group is linked. Add creators to start.',
          type: 'success',
          icon: 'success',
        });
      },
      error: () => {
        this.busy.set(false);
        this.toast.alert({
          title: 'Whoops! Something went wrong.',
          message: 'Please try again.',
          type: 'error',
          icon: 'error',
        });
      },
    });
  }
}
